import { buildContentPdfRecipesMegistral } from '@clinical/medicines/utils/build-pdf-recipes-magistral';
import { MEDICINES_ROUTES } from '@clinical/medicines/medicines-routes.module';
import { DEFAULT_PDF_POSITION_SIGNATURE } from '@shared/constants/app.const';
import { PrintedOnPage, PrintMode, Workflow } from '@shared/interfaces/paperwork.interface';

import { DELETE_RECIPE, PUT_RECIPE } from '../store/clinical-recipe.actions';
import { buildContentPdfRecipes } from '../utils/build-pdf-recipes';

/**
 * Configuración del flujo de trabajo (workflow) para el trámite de recetas médicas.
 *
 * @description
 * Define el comportamiento y las características del proceso de generación de recetas,
 * incluyendo requisitos de firma, almacenamiento, y notificaciones al paciente.
 *
 * @type {Workflow}
 *
 * @property {boolean} isSigned - Indica si el documento requiere firma electrónica
 * @property {boolean} isStored - Indica si el documento se almacena en el sistema
 * @property {boolean} isSaving - Indica si el documento está en proceso de guardado
 * @property {boolean} hasNextControl - Indica si se debe programar un control de seguimiento
 * @property {Array} notifications - Array de configuraciones de notificaciones por email al paciente
 */
const workflow: Workflow = {
  isSigned: true,
  isStored: true,
  isSaving: true,
  hasNextControl: true,
  notifications: [
    {
      type    : 'email',
      entity  : 'patient',
      subject : 'Notificaciones UC CHRISTUS | Receta Médica',
      template: 'recipe',
    },
  ],
};
/**
 * Configuración de generación de PDF para documentos de recetas médicas.
 *
 * @description
 * Define la estructura y elementos visuales que se incluirán en el PDF de la receta,
 * incluyendo encabezado, información del paciente, firma digital y código QR para
 * validación en el portal de farmacias.
 *
 * @property {boolean} isPreview - Indica si se debe mostrar una vista previa antes de generar el PDF
 * @property {Object} header - Configuración del encabezado (logo, título, datos del profesional)
 * @property {Object} patient - Configuración de visualización de datos del paciente
 * @property {Object} footer - Configuración del pie de página incluyendo firma digital y código QR
 */
const pdf                = {
  isPreview: true,
  header   : {logo: PrintedOnPage.all, title: PrintedOnPage.all, professional: PrintedOnPage.all},
  patient  : {type: PrintMode.full, printed: PrintedOnPage.all},
  footer   : {
    signature: {printed: PrintedOnPage.all, position: DEFAULT_PDF_POSITION_SIGNATURE},
    qr: {
      right: {
        title: 'Portal Farmacia',
        url: 'https://portalfarmacias.ucchristus.cl/portal/block-portal/$shortId$',
      },
    },
  },
};

/**
 * Definición completa de configuración para los diferentes tipos de recetas médicas.
 *
 * @description
 * Objeto centralizado que contiene todas las configuraciones necesarias para manejar
 * los distintos tipos de recetas médicas (general, diabetes, magistral), incluyendo
 * sus acciones en el store, rutas, generación de PDFs, workflows y metadatos.
 *
 * @property {Object} actions - Acciones Redux para crear y eliminar recetas
 * @property {string} prefix - Prefijo utilizado para identificadores de recetas médicas electrónicas
 * @property {string} module - Nombre del módulo al que pertenecen las recetas
 * @property {Object} receta - Configuración para recetas médicas generales
 * @property {Object} diabetes - Configuración para recetas específicas de diabetes
 * @property {Object} magistral - Configuración para recetas magistrales (preparaciones farmacéuticas)
 *
 * @remarks
 * Cada tipo de receta incluye:
 * - suffix: Sufijo identificador único
 * - group: Categoría de agrupación
 * - docTitle: Título del documento
 * - submodule: Ruta del submódulo
 * - getBodyDefinition: Función para construir el contenido del PDF
 * - workflow: Configuración del flujo de trabajo
 * - config: Configuración de generación del PDF
 */
export const PAPERWORK_RECIPES_DEFINITION = {
  actions: {
    put   : PUT_RECIPE,
    delete: DELETE_RECIPE
  },
  prefix : 'RME',
  module : 'recetas',
  receta: {
    suffix           : 'RMG',
    group            : 'receta médica',
    docTitle         : 'Receta Médica',
    submodule: MEDICINES_ROUTES.generalRecipe,
    getBodyDefinition: buildContentPdfRecipes,
    workflow,
    config           : pdf,
  },

  diabetes: {
    suffix           : 'RMD',
    group            : 'receta diabetes',
    docTitle         : 'Receta Médica',
    submodule: MEDICINES_ROUTES.diabetesRecipe,
    getBodyDefinition: buildContentPdfRecipes,
    workflow,
    config           : pdf,
  },

  magistral: {
    suffix           : 'RMM',
    group            : 'receta magistral',
    docTitle         : 'receta magistral',
    submodule: MEDICINES_ROUTES.masterRecipe,
    getBodyDefinition: buildContentPdfRecipesMegistral,
    workflow,
    config           : pdf,
  },
};
